import { getDateRangeOptions } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import {
  luxonDateSearchParam,
  numberSearchParam,
  stringListSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useLazyListAdminStoresOverallQuery } from 'api/client-domain/client-domain';
import { useLazyListCampaignsQuery } from 'api/offers-domain/offers-domain';
import {
  useGetJobsStatusQuery,
  useLazyGetPulseMessagesQuery,
  useLazyGetPulseOffersQuery,
} from 'api/reports-service/reports-service';
import { getPreviousPeriod } from 'components/search/search-filters/date-filter/date-filter.utils';
import { getMerchantId } from 'util/merchant-id';

type SearchParamTypes =
  | 'date'
  | 'comparisondate'
  | 'storeIds'
  | 'campaignIds'
  | 'messageIds'
  | 'offerIds'
  | 'status'
  | 'emailType';

type SearchParamsConfig = {
  [key in SearchParamTypes]: boolean;
};

const useDefaultStartAndEndDate = () => {
  const { data: jobsStatusData } = useGetJobsStatusQuery();
  const emailInsightsMaxDate = jobsStatusData?.data.find(
    j => j.jobName === 'EMAIL_INSIGHTS',
  )?.dataAvailableDate;

  if (emailInsightsMaxDate) {
    return [
      DateTime.fromISO(emailInsightsMaxDate).minus({ days: 7 }).startOf('day'),
      DateTime.fromISO(emailInsightsMaxDate).endOf('day'),
    ];
  }
  return [
    DateTime.local().minus({ days: 9 }).startOf('day'),
    DateTime.local().minus({ days: 2 }).endOf('day'),
  ];
};

export const useDateParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [defaultStartDate, defaultEndDate] = useDefaultStartAndEndDate();
  return [
    luxonDateSearchParam('startdate', {
      defaultValue: defaultStartDate,
      filter: isFilterVisible
        ? {
            type: 'DateRangeFilter',
            label: t('forms.fields.dateRange.label'),
            showName: true,
            startParamName: 'startdate',
            endParamName: 'enddate',
            predefinedDateOptions: getDateRangeOptions([
              'lpLast7Days',
              'lpLast30Days',
              'lpLast60Days',
              'lpLast90Days',
              'lpLast365Days',
            ]),
            componentProps: {
              dateRangeBaseField: { disabled: true },
            },
            clearable: false,
            maxDate: defaultEndDate,
            anchorDate: defaultEndDate,
          }
        : undefined,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
    luxonDateSearchParam('enddate', {
      defaultValue: defaultEndDate,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
  ];
};

export const useComparisonDateParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [defaultStartDate, defaultEndDate] = useDefaultStartAndEndDate();
  const [comparisonStartDate, comparisonEndDate] = getPreviousPeriod(
    defaultStartDate,
    defaultEndDate,
  );
  return [
    luxonDateSearchParam('comparisonstartdate', {
      defaultValue: comparisonStartDate,
      filter: isFilterVisible
        ? {
            type: 'ComparisonDateRangeFilter',
            label: t('forms.fields.compareTo.label'),
            showName: true,
            startParamName: 'comparisonstartdate',
            endParamName: 'comparisonenddate',
            compareStartParamName: 'startdate',
            compareEndParamName: 'enddate',
            componentProps: {
              selectBase: { disabled: true },
              dateRangeBaseField: { disabled: true },
            },
            clearable: false,
          }
        : undefined,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
    luxonDateSearchParam('comparisonenddate', {
      defaultValue: comparisonEndDate,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
  ];
};

export const useLocationParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerStores] = useLazyListAdminStoresOverallQuery();

  return [
    stringListSearchParam('storeIds', {
      defaultValue: ['ALL'],
      'data-testid': 'LocationsFilter',
      options: [
        {
          label: t('page.transactions.filters.statusFilter.all.label'),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const response = await triggerStores({ merchantid: getMerchantId() });
        const dataArray = response?.data?.stores.map(store => ({
          label: store.title,
          id: store.storeId,
        }));

        return [
          {
            label: t('page.transactions.filters.statusFilter.all.label'),
            id: 'ALL',
          },
        ].concat(dataArray ?? []);
      },
      filter: isFilterVisible
        ? {
            type: 'MultiSelectFilter',
            label: t('page.emailPulse.filters.locations.label'),
          }
        : undefined,
    }),
  ];
};

export const useCampaignParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerCampaigns] = useLazyListCampaignsQuery();

  return [
    stringListSearchParam('campaignIds', {
      defaultValue: ['ALL'],
      'data-testid': 'CampaignsFilter',
      options: [
        {
          label: t('page.emailPulse.filters.campaign.all'),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const response = await triggerCampaigns({ count: 1000, page: 0 });
        const dataArray = response?.data?.campaigns.map(campaign => ({
          label: campaign.title,
          id: campaign.campaignId,
        }));

        return [
          {
            label: t('page.emailPulse.filters.campaign.all'),
            id: 'ALL',
          },
        ].concat(dataArray ?? []);
      },
      filter: isFilterVisible
        ? {
            type: 'MultiSelectFilter',
            label: t('page.emailPulse.filters.campaign.label'),
          }
        : undefined,
    }),
  ];
};

export const useMessageParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerMessages] = useLazyGetPulseMessagesQuery();

  return [
    stringListSearchParam('messageIds', {
      defaultValue: ['ALL'],
      'data-testid': 'MessagesFilter',
      options: [
        {
          label: t('page.emailPulse.filters.message.all'),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const response = await triggerMessages();
        const dataArray = response?.data?.map(message => ({
          label: message.title,
          id: message.messageId,
        }));

        return [
          {
            label: t('page.emailPulse.filters.message.all'),
            id: 'ALL',
          },
        ].concat(dataArray ?? []);
      },
      filter: isFilterVisible
        ? {
            type: 'MultiSelectFilter',
            label: t('page.emailPulse.filters.message.label'),
          }
        : undefined,
    }),
  ];
};

export const useOfferParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerOffers] = useLazyGetPulseOffersQuery();

  return [
    stringListSearchParam('offerIds', {
      defaultValue: ['ALL'],
      'data-testid': 'OffersFilter',
      options: [
        {
          label: t('page.emailPulse.filters.offer.all'),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const response = await triggerOffers();
        const dataArray = response?.data?.map(offer => ({
          label: offer.title,
          id: offer.offerId,
        }));

        return [
          {
            label: t('page.emailPulse.filters.offer.all'),
            id: 'ALL',
          },
        ].concat(dataArray ?? []);
      },
      filter: isFilterVisible
        ? {
            type: 'MultiSelectFilter',
            label: t('page.emailPulse.filters.offer.label'),
          }
        : undefined,
    }),
  ];
};

export const useStatusParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();

  const statusOptions = [
    {
      label: t('page.emailPulse.filters.status.active'),
      id: 'ACTIVE',
    },
    {
      label: t('page.emailPulse.filters.status.expired'),
      id: 'EXPIRED',
    },
    {
      label: t('page.emailPulse.filters.status.all'),
      id: 'ALL',
    },
  ];

  return [
    stringSearchParam('status', {
      defaultValue: 'ACTIVE',
      options: statusOptions,
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.emailPulse.filters.status.label'),
          }
        : undefined,
    }),
  ];
};

export const useEmailTypeParams = (
  isFilterVisible: boolean,
  category: 'ORDERS' | 'GIFT_CARDS' | 'SECURITY',
) => {
  const { t } = useTranslation();

  // Import dynamically to avoid circular dependencies
  const { getEmailTypeOptions } = require('./email-pulse-email-type-mappings');
  const emailTypeOptions = getEmailTypeOptions(category);

  return [
    stringSearchParam('emailType', {
      defaultValue: 'ALL',
      options: emailTypeOptions,
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.emailPulse.filters.emailType.label'),
          }
        : undefined,
      excludeFromUrl: true,
    }),
  ];
};

const useAllSearchParams = (
  searchParamsConfig: SearchParamsConfig,
  sortBy: string,
  emailTypeCategory: 'ORDERS' | 'GIFT_CARDS' | 'SECURITY' | null = null,
) => {
  // Always call hooks in the same order - just pass false if not needed
  const emailTypeParams = useEmailTypeParams(
    emailTypeCategory ? searchParamsConfig?.emailType : false,
    emailTypeCategory || 'ORDERS',
  );

  return [
    ...useDateParams(searchParamsConfig?.date),
    ...useComparisonDateParams(searchParamsConfig?.comparisondate),
    ...useLocationParams(searchParamsConfig?.storeIds),
    ...useCampaignParams(searchParamsConfig?.campaignIds),
    ...useMessageParams(searchParamsConfig?.messageIds),
    ...useOfferParams(searchParamsConfig?.offerIds),
    ...useStatusParams(searchParamsConfig?.status),
    ...(emailTypeCategory ? emailTypeParams : []),
    // Related to https://app.shortcut.com/incentivio/story/346328/allow-sorting-by-selected-column-in-location-and-offer-performance-reports
    // We are replacing defaultSearchParams so that we can prevent sort params from persisting across tabs. Persisting these search params
    // would have undesirable side effects. This solution isn't ideal either, but we consider it to be the lesser of two evils.
    ...[
      numberSearchParam('count', {
        defaultValue: 100,
      }),
      numberSearchParam('page', {
        defaultValue: 0,
      }),
      stringSearchParam('sortBy', {
        defaultValue: sortBy,
        excludeFromUrl: true,
      }),
      stringSearchParam('sortDirection', {
        defaultValue: 'asc',
        excludeFromUrl: true,
      }),
    ],
  ];
};

// Overview: Date Range, Comparison (no filters visible)
export const useOverviewSearchParams = () => {
  return useAllSearchParams(
    {
      date: true,
      comparisondate: true,
      storeIds: false,
      campaignIds: false,
      messageIds: false,
      offerIds: false,
      status: false,
      emailType: false,
    },
    'emailsSent',
  );
};

// Messages: Date Range, Comparison, Locations, Campaigns, Messages, Status
export const useMessagesSearchParams = () => {
  return useAllSearchParams(
    {
      date: true,
      comparisondate: true,
      storeIds: true,
      campaignIds: true,
      messageIds: true,
      offerIds: false,
      status: true,
      emailType: false,
    },
    'messageTitle',
  );
};

// Offers: Date Range, Comparison, Locations, Campaigns, Offers, Status
export const useOffersSearchParams = () => {
  return useAllSearchParams(
    {
      date: true,
      comparisondate: true,
      storeIds: true,
      campaignIds: true,
      messageIds: false,
      offerIds: true,
      status: true,
      emailType: false,
    },
    'offerTitle',
  );
};

// Orders: Date Range, Comparison, Locations, Email Type (transactional, no campaigns)
export const useOrdersSearchParams = () => {
  return useAllSearchParams(
    {
      date: true,
      comparisondate: true,
      storeIds: true,
      campaignIds: false,
      messageIds: false,
      offerIds: false,
      status: false,
      emailType: true,
    },
    'emailTypeDisplay',
    'ORDERS',
  );
};

// Gift Cards: Date Range, Comparison, Email Type (transactional, no campaigns)
export const useGiftCardsSearchParams = () => {
  return useAllSearchParams(
    {
      date: true,
      comparisondate: true,
      storeIds: false,
      campaignIds: false,
      messageIds: false,
      offerIds: false,
      status: false,
      emailType: true,
    },
    'emailTypeDisplay',
    'GIFT_CARDS',
  );
};

// Security: Date Range, Comparison, Email Type
export const useSecuritySearchParams = () => {
  return useAllSearchParams(
    {
      date: true,
      comparisondate: true,
      storeIds: false,
      campaignIds: false,
      messageIds: false,
      offerIds: false,
      status: false,
      emailType: true,
    },
    'emailTypeDisplay',
    'SECURITY',
  );
};
